/**
 * 只读守卫：当前路由被授权为「只读」时，自动禁用页面内的写操作控件。
 *
 * 设计取舍：不逐页改造几十个 HIS 页面，而是在 body 上挂 MutationObserver，
 * 按控件文案识别写操作按钮并置灰。弹窗（el-dialog append-to-body）挂在 body 下，
 * 所以监听范围是整个 body，但实际置灰只作用于内容区与浮层（见 SCOPE_SELECTORS）。
 *
 * 这是「防误操作」而非「防攻击」——真正的硬拦截需要后端按菜单校验写请求，
 * 见 menu-access.js 顶部说明。
 */

/** 文案命中即视为写操作 */
const WRITE_TEXTS = [
    '新增', '添加', '新建', '创建', '录入', '开单', '登记',
    '编辑', '修改', '更新', '变更', '调整',
    '删除', '移除', '清空', '作废', '撤销', '撤回', '取消', '终止', '停用', '启用',
    '保存', '提交', '确认', '审核', '审批', '通过', '驳回',
    '收费', '退费', '结算', '预结算', '充值', '退款',
    '入库', '出库', '发药', '退药', '盘点', '调拨',
    '导入', '上传', '同步', '生成', '执行', '开始', '完成',
    '绑定', '解绑', '关联', '设为', '重置', '补录', '补开'
];

/**
 * 文案命中则强制放行，优先级高于 WRITE_TEXTS（解决「取消」等歧义）。
 * 注意「确定」不在写操作名单里：它是弹窗通用确认按钮，只读态下更多出现在
 * 查看/筛选类弹窗，置灰会误伤查询；真正的写入口已被置灰，写请求另有拦截器兜底。
 */
const READ_TEXTS = [
    '查询', '搜索', '重置查询', '筛选', '刷新', '导出', '打印', '预览',
    '详情', '查看', '明细', '返回', '关闭', '收起', '展开', '下载'
];

const READONLY_TITLE = '当前角色对该菜单为只读权限，不可操作';
const MARK_ATTR = 'data-readonly-guard';
const PREV_DISPLAY_ATTR = 'data-readonly-prev-display';

/**
 * 只读时对写操作控件的处理方式：
 *   'hide'    直接隐藏（默认）
 *   'disable' 置灰但保留
 *
 * 默认隐藏的原因：页面本身已有「按业务状态禁用」的置灰按钮（如已收费的医嘱不可删除、
 * 非初始状态不可停用），再叠一层权限置灰，用户分不清是没权限还是状态不允许。
 * 权限不足属于「你没有这个功能」，不该占着位置。
 */
const MODE = 'hide';

let observer = null;
let active = false;
let pending = null;

function textOf(el) {
    return (el.innerText || el.textContent || '').replace(/\s/g, '');
}

/**
 * 「取消 / 关闭 / 取消编辑」是放弃当前操作或关掉弹窗，不是业务撤销，一律放行。
 * 业务撤销走「撤销XX」「取消挂号」这类带宾语的文案，不受影响。
 */
const CANCEL_TEXTS = ['取消', '关闭', '关 闭', '取消编辑', '清空条件', '重置查询'];

function isDialogCancel(el, text) {
    return CANCEL_TEXTS.indexOf(text) !== -1;
}

/**
 * 名词后缀：以这些字眼结尾的按钮多是「查看某某」的入口而非动作，
 * 例如「收费项目」「变更日志」「基金结算清单」——虽然含写动词，但点开只是弹窗看数据。
 */
const READ_SUFFIXES = [
    '项目', '明细', '清单', '日志', '记录', '信息', '详情', '列表',
    '结果', '报表', '统计', '条件', '说明', '历史', '数据', '状态'
];

/**
 * 强写动词前缀：以这些开头就是真动作，即使后面跟着名词后缀也照样禁用，
 * 例如「添加收费项目」「新增调整单」「生成盘点单」。
 */
const STRONG_WRITE_PREFIXES = [
    '添加', '新增', '新建', '创建', '删除', '移除', '编辑', '修改', '保存',
    '提交', '导入', '上传', '生成', '绑定', '解绑', '录入', '确认', '批量',
    '一键', '立即', '设为', '同步', '执行', '撤销', '作废', '停用', '启用',
    '补录', '补开', '手工', '重新'
];

function endsWithReadSuffix(text) {
    return READ_SUFFIXES.some(suffix => text.endsWith(suffix));
}

function startsWithStrongWrite(text) {
    return STRONG_WRITE_PREFIXES.some(prefix => text.indexOf(prefix) === 0);
}

function shouldDisable(el) {
    const text = textOf(el);
    if (!text) return false;
    if (READ_TEXTS.some(t => text.indexOf(t) !== -1)) return false;
    if (isDialogCancel(el, text)) return false;
    // 名词短语视为查看入口，除非以强写动词开头
    if (endsWithReadSuffix(text) && !startsWithStrongWrite(text)) return false;
    return WRITE_TEXTS.some(t => text.indexOf(t) !== -1);
}

function disable(el) {
    if (el.getAttribute(MARK_ATTR) === '1') return;
    el.setAttribute(MARK_ATTR, '1');

    if (MODE === 'hide') {
        // 记下原有的行内 display，还原时才不会破坏页面自身的显隐逻辑
        el.setAttribute(PREV_DISPLAY_ATTR, el.style.display || '');
        el.style.display = 'none';
        return;
    }

    el.classList.add('is-disabled');
    el.setAttribute('disabled', 'disabled');
    el.style.pointerEvents = 'none';
    el.style.opacity = '0.55';
    el.title = READONLY_TITLE;
}

function restore(el) {
    el.removeAttribute(MARK_ATTR);

    if (el.hasAttribute(PREV_DISPLAY_ATTR)) {
        el.style.display = el.getAttribute(PREV_DISPLAY_ATTR);
        el.removeAttribute(PREV_DISPLAY_ATTR);
    }

    el.classList.remove('is-disabled');
    el.removeAttribute('disabled');
    el.style.pointerEvents = '';
    el.style.opacity = '';
    if (el.title === READONLY_TITLE) el.title = '';
}

/**
 * 扫描范围：页面内容区 .main + body 下的浮层（弹窗/抽屉/气泡确认）。
 * 不扫全局顶栏，避免误伤与当前菜单无关的全局操作。
 */
const SCOPE_SELECTORS = [
    '.main',
    '.el-dialog',
    '.el-drawer',
    '.el-message-box',
    '.el-popover',
    '.el-popconfirm',
    // 下拉菜单默认挂到 body 上，不在 .main 里，必须单独纳入
    '.el-dropdown-menu'
];
// 表格「更多」里的操作项是 el-dropdown-item（渲染成 li），不是按钮，得单独匹配
const CONTROL_SELECTOR = '.el-button, button, .el-link, .el-dropdown-menu__item';

function scan() {
    pending = null;
    if (!active) return;
    const scopes = document.querySelectorAll(SCOPE_SELECTORS.join(','));
    for (let i = 0; i < scopes.length; i++) {
        const nodes = scopes[i].querySelectorAll(CONTROL_SELECTOR);
        for (let j = 0; j < nodes.length; j++) {
            if (shouldDisable(nodes[j])) disable(nodes[j]);
        }
    }
}

function scheduleScan() {
    if (pending) return;
    pending = window.setTimeout(scan, 60);
}

/** 进入只读路由时调用 */
export function startReadonlyGuard() {
    if (active) return;
    active = true;
    document.body.classList.add('menu-readonly');
    scan();
    if (!observer) {
        observer = new MutationObserver(scheduleScan);
    }
    observer.observe(document.body, { childList: true, subtree: true });
}

/** 离开只读路由时调用，把之前置灰的控件还原 */
export function stopReadonlyGuard() {
    if (!active) return;
    active = false;
    document.body.classList.remove('menu-readonly');
    if (observer) observer.disconnect();
    if (pending) {
        window.clearTimeout(pending);
        pending = null;
    }
    const marked = document.querySelectorAll('[' + MARK_ATTR + '="1"]');
    for (let i = 0; i < marked.length; i++) {
        restore(marked[i]);
    }
}

/** 按路由的只读状态切换守卫 */
export function syncReadonlyGuard(readonly) {
    if (readonly) {
        startReadonlyGuard();
    } else {
        stopReadonlyGuard();
    }
}
